import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Shield, Database, Server, RefreshCw, ChevronDown, ChevronUp, CheckCircle, Download } from 'lucide-react';
import { admin as adminApi } from '../utils/api';

const SystemInfoPanel = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);

  const loadInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getSystemInfo();
      setInfo(res.data);
    } catch (err) {
      console.warn('System info not available');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const exportConfig = async () => {
    setExporting(true);
    try {
      const res = await adminApi.exportConfig();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `morphbook-config-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Configuration exported!');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const runMigrations = async () => {
    setRunning(true);
    try {
      const res = await adminApi.runMigrations();
      toast.success(res.data.message || 'Migrations completed');
      await loadInfo();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Migration failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center space-x-2">
          <Shield size={24} className="text-primary-600" />
          <span>System Information</span>
        </h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={exportConfig}
            disabled={exporting}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            title="Export configuration to JSON"
          >
            <Download size={15} />
            <span>{exporting ? 'Exporting...' : 'Export Config'}</span>
          </button>
          <button onClick={loadInfo} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors" title="Refresh">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {loading && !info ? (
        <div className="text-gray-500 text-sm">Loading system info...</div>
      ) : info ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
              <div className="text-xs text-primary-600 font-semibold uppercase tracking-wide mb-1">App Version</div>
              <div className="text-2xl font-bold text-primary-700">v{info.version}</div>
              {info.release_date && <div className="text-xs text-primary-500 mt-1">Released {info.release_date}</div>}
            </div>
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1 flex items-center space-x-1">
                <Server size={12} /><span>PHP Version</span>
              </div>
              <div className="text-xl font-bold text-gray-800">{info.php_version}</div>
            </div>
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1 flex items-center space-x-1">
                <Database size={12} /><span>Database</span>
              </div>
              <div className="text-xl font-bold text-gray-800">{info.db_size ?? 'N/A'}</div>
              {info.uploads_size && <div className="text-xs text-gray-500 mt-1">Uploads: {info.uploads_size}</div>}
            </div>
          </div>

          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-800 flex items-center space-x-2">
                  <CheckCircle size={18} className="text-green-500" />
                  <span>Database Migrations</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {info.migrations_applied} migration{info.migrations_applied !== 1 ? 's' : ''} applied
                  {info.latest_migration && (
                    <span className="ml-2 text-gray-400">· Latest: <span className="font-mono text-xs">{info.latest_migration.name}</span></span>
                  )}
                </div>
              </div>
              <button onClick={runMigrations} disabled={running} className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <RefreshCw size={16} className={running ? 'animate-spin' : ''} />
                <span>{running ? 'Running...' : 'Run Migrations'}</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Run this after updating to a new version. Safe to run multiple times — already-applied migrations are skipped.
            </p>
          </div>

          {info.changelog && info.changelog.length > 0 && (
            <div>
              <button onClick={() => setShowChangelog(v => !v)} className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900">
                {showChangelog ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                <span>Version History ({info.changelog.length})</span>
              </button>
              {showChangelog && (
                <div className="mt-3 space-y-2">
                  {info.changelog.map((entry, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center space-x-3">
                        <span className="font-mono text-sm font-bold text-primary-600">v{entry.version}</span>
                        {entry.date && <span className="text-xs text-gray-400">{entry.date}</span>}
                      </div>
                      {entry.notes && <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
            <div className="font-semibold text-yellow-800 mb-1">How to update this software</div>
            <ol className="text-yellow-700 space-y-1 list-decimal list-inside">
              <li>Back up your database.sqlite file and uploads/ folder</li>
              <li>Download the new version and overwrite all files except .env, database.sqlite, and uploads/</li>
              <li>Click Run Migrations above to apply any schema changes</li>
              <li>Hard-refresh your browser (Ctrl+Shift+R)</li>
            </ol>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SystemInfoPanel;